import { Box } from "arkh";
import React, { useCallback, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { WidgetSignalOverview } from "widgets";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";
import { getWidgetAddWidgetSizeWithinBounds } from "../utils/useWidgetAddWidgetSizeState";
import { WACategoryScreenProps } from "../WANavigation";
import { WACategoryWidgetContainer } from "../WACategoryWidgetContainer";
import { WACategorySignalsQuery } from "./__generated__/WACategorySignalsQuery.graphql";
import { overviewWidgetSizes } from "../utils/widgetSizes/overviewWidgetSizes";
import { FormattedMessage } from "react-intl";
import { useWACategoryLayout } from "../utils/useWACategoryLayout";

type Props = WACategoryScreenProps<"WACategorySignals">;
export function WACategorySignals(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerWACategorySignals {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWACategorySignals(props: Props & { fetchKey: string }) {
  const intl_tokens = useIntlTokens();

  const data = useLazyLoadQuery<WACategorySignalsQuery>(
    graphql`
      query WACategorySignalsQuery(
        $storecastId: ID!
        $input: PreviewWidgetSignalOverviewInput!
      ) {
        dashboard(id: $storecastId) @required(action: THROW) {
          previewWidgetSignalOverview(input: $input) {
            ...WidgetSignalOverview_widget
          }
        }
      }
    `,
    {
      input: {
        size: "TWO_X_TWO",
        teamIds: props.route.params.defaultTeams,
      },
      storecastId: props.route.params.storecastId,
    },
    { fetchKey: props.fetchKey }
  );

  const [size, hasSizeWithinBounds] = useMemo(
    () =>
      getWidgetAddWidgetSizeWithinBounds({
        maxSize: props.route.params.maxSize,
        widgetSizes: overviewWidgetSizes,
      }),
    [props.route.params.maxSize]
  );

  const onAdd = useCallback(() => {
    props.navigation.navigateDeprecated("WidgetAddSignals", {
      defaultTeams: props.route.params.defaultTeams,
      position: props.route.params.position,
      storecastId: props.route.params.storecastId,
      unitSize: props.route.params.unitSize,
      maxSize: props.route.params.maxSize,
    });
  }, [
    props.navigation,
    props.route.params.defaultTeams,
    props.route.params.maxSize,
    props.route.params.position,
    props.route.params.storecastId,
    props.route.params.unitSize,
  ]);

  const layout = useWACategoryLayout();

  return (
    <Box
      style={[
        layout,
        {
          flex: 1,
          justifyContent: "flex-start",
          alignItems: "center",
        },
      ]}
    >
      <WACategoryWidgetContainer
        hasSizeWithinBounds={hasSizeWithinBounds}
        size={size}
        title={intl_tokens.Signals}
        unitSize={props.route.params.unitSize}
        onAdd={onAdd}
        addTitle={intl_tokens.SelectTeams}
        description={
          <FormattedMessage
            description="WA Signals Description"
            defaultMessage="Display urgent signals and updates from your teams in one central location"
          />
        }
      >
        <WidgetSignalOverview
          widget={data.dashboard.previewWidgetSignalOverview}
          storecastId={props.route.params.storecastId}
          mode="IDLE"
        />
      </WACategoryWidgetContainer>
    </Box>
  );
}
